import { useEffect, useMemo, useRef } from 'react';
import * as echarts from 'echarts';
import type { EChartsType } from 'echarts';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '../lib/api';

export function FragsDistanceChart({ theme }: { theme: 'light' | 'dark' }): JSX.Element {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<EChartsType | null>(null);
  const { data: fragsResponse, isLoading } = useQuery({
    queryKey: ['frags-distance'],
    queryFn: () =>
      apiFetch<{
        distances: number[];
        series: {
          '7.62 SNP': number[];
          Rifle: number[];
          MBT: number[];
          '12.7 SNP': number[];
        };
      }>('/frags-distance/'),
  });

  useEffect(() => {
    if (!chartRef.current) return;
    chartInstance.current?.dispose();
    const instance = echarts.init(chartRef.current);
    chartInstance.current = instance;

    const resizeHandler = () => instance.resize();
    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
      instance.dispose();
    };
  }, [theme]);

  const labels = useMemo(
    () => fragsResponse?.distances.map((value) => value.toString()) ?? [],
    [fragsResponse]
  );
  const series = useMemo(
    () => [
      { name: 'Ак/M4/M16', data: fragsResponse?.series.Rifle ?? [], colorToken: '--success' },
      { name: 'Сн. винт.', data: fragsResponse?.series['7.62 SNP'] ?? [], colorToken: '--accent' },
      { name: 'Сн. винт. 12.7 мм', data: fragsResponse?.series['12.7 SNP'] ?? [], colorToken: '--destructive' },
      { name: 'Танки', data: fragsResponse?.series.MBT ?? [], colorToken: '--ring' },
    ],
    [fragsResponse]
  );

  useEffect(() => {
    if (!chartInstance.current) return;

    const styles = getComputedStyle(document.documentElement);
    const toHsl = (token: string) => `hsl(${styles.getPropertyValue(token).trim()})`;

    const axisColor = toHsl('--border');
    const textColor = toHsl('--muted-foreground');
    const cardColor = toHsl('--card');

    const option = {
      animation: false,
      tooltip: {
        trigger: 'axis',
        backgroundColor: cardColor,
        borderColor: axisColor,
        textStyle: { color: toHsl('--foreground') },
        confine: true,
        axisPointer: {
          type: 'shadow',
          snap: true,
          animation: false,
          label: { show: false },
        },
      },
      legend: {
        top: 8,
        textStyle: { color: textColor },
      },
      grid: { left: 52, right: 16, top: 48, bottom: 64 },
      dataZoom: [
        { type: 'slider', start: 0, end: 50, height: 18, bottom: 24, borderColor: axisColor, textStyle: { color: textColor } },
        { type: 'inside' },
      ],
      xAxis: {
        type: 'category',
        data: labels,
        name: 'Дистанция, м',
        axisLine: { lineStyle: { color: axisColor } },
        axisLabel: { color: textColor, interval: 9 },
        nameTextStyle: { color: textColor },
      },
      yAxis: {
        type: 'value',
        name: 'Кол-во фрагов',
        axisLine: { lineStyle: { color: axisColor } },
        splitLine: { lineStyle: { color: axisColor, type: 'dashed' } },
        axisLabel: { color: textColor },
        nameTextStyle: { color: textColor },
      },
      series: series.map((item, idx) => ({
        name: item.name,
        type: 'bar',
        data: item.data,
        large: true,
        itemStyle: { color: toHsl(item.colorToken) },
        emphasis: { focus: 'none', scale: false, disabled: true },
        barWidth: idx === 0 ? 9 : 7,
        barGap: '35%',
        barCategoryGap: '28%',
        animation: false,
      })),
    } as echarts.EChartsOption;

    chartInstance.current.setOption(option, true);
  }, [series, theme]);

  return (
    <div className="card p-4 space-y-4">
      <div>
        <p className="text-2xl font-semibold">Фраги и дистанция</p>
        <p className="text-sm text-muted-foreground">Распределение фрагов по дистанции</p>
      </div>
      <motion.div
        className="relative h-[420px]"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div ref={chartRef} className="h-full w-full" />
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-end gap-2">
              {[0, 1, 2, 3].map((index) => (
                <motion.div
                  key={index}
                  className="w-2 rounded-sm bg-primary/70"
                  animate={{ height: [12, 36, 16] }}
                  transition={{ duration: 1.2, repeat: Infinity, delay: index * 0.15 }}
                />
              ))}
            </div>
          </div>
        ) : null}
      </motion.div>
    </div>
  );
}
